"use client"

import { useState, useEffect, useContext } from "react"
import { useTheme } from "@mui/material/styles"
import { 
  IconButton, 
  TextField, 
  Button, 
  Grid, 
  Typography, 
  Paper, 
  FormControlLabel, 
  Checkbox, 
  Link, 
  Alert, 
  Fade, 
  CircularProgress, 
  Box
} from "@mui/material"
import { Visibility, VisibilityOff, LockOutlined, EmailOutlined } from "@mui/icons-material"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, Link as RouterLink } from "react-router-dom"
import { loginUser, loadUser } from "../../redux/actions/user"
import logo from "../../assets/6-removebg.png"
import { tokens } from "../../theme"

function Login() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { user, isAuthenticated, error: authError } = useSelector((state) => state.user)

  const handleSignIn = async (e) => {
    e.preventDefault()
    setError("")
    setIsSubmitting(true)
    
    // Basic validation
    if (!email || !password) {
      setError("Please fill in all fields")
      setIsSubmitting(false)
      return
    }
    
    try {
      setIsLoading(true)
      await dispatch(loginUser(email, password, false))
    } catch (err) {
      setError(err.message || "An error occurred during login")
    } finally {
      setIsLoading(false)
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === "superadmin") {
        navigate("/dashboard")
      } else if (user.shop) {
        switch (user.shop.enum) {
          case "electronics":
            navigate("/produit")
            break
          case "alimentation":
            navigate("/produit-alimentation")
            break
          default:
            navigate("/dashboard")
        }
      } else {
        navigate("/dashboard")
      }
    }
  }, [isAuthenticated, user, navigate, dispatch])

  useEffect(() => {
    dispatch(loadUser())
  }, [dispatch])

  // Handle auth errors
  useEffect(() => {
    if (authError) {
      setError(authError)
    }
  }, [authError])

  return (
    <div>
      <Grid
        container
        sx={{
          minHeight: "100vh",
          background: theme.palette.mode === 'dark' 
            ? `linear-gradient(135deg, ${colors.primary[900]} 0%, ${colors.grey[900]} 100%)`
            : `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.grey[100]} 100%`,
          alignItems: "center",
          justifyContent: "center",
          padding: 2,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-50%",
            left: "-50%",
            width: "200%",
            height: "200%",
            background: "radial-gradient(circle, rgba(34, 197, 94, 0.1) 0%, transparent 70%)",
            animation: "pulse 15s infinite alternate",
            "@keyframes pulse": {
              "0%": { transform: "scale(1)", opacity: 0.3 },
              "100%": { transform: "scale(1.1)", opacity: 0.5 }
            }
          }
        }}
      >
      {/* Left Section */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: theme.palette.mode === 'dark' ? colors.grey[100] : colors.grey[900],
          textAlign: "center",
          padding: 4,
        }}
      >
        <Box
          component="div"
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? colors.grey[800] : 'white',
            marginBottom: "20px",
            borderRadius: "16px",
            boxShadow: theme.palette.mode === 'dark'
              ? `0 12px 28px ${colors.primary[900]}40`
              : "0 12px 28px rgba(34, 197, 94, 0.2)",
            padding: "8px",
            width: "100px",
            height: "100px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <img
            src={logo || "/placeholder.svg"}
            alt="Logo"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain"
            }}
          />
        </Box>
        <Typography
          variant="h3"
          sx={{
            fontWeight: "bold",
            marginBottom: 2,
            textShadow: theme.palette.mode === 'dark' 
              ? `2px 2px 5px ${colors.primary[900]}` 
              : "2px 2px 5px rgba(0,0,0,0.3)",
            color: theme.palette.mode === 'dark' ? colors.grey[100] : colors.grey[900],
          }}
        >
          Content de vous revoir !
        </Typography>
        <Typography
          sx={{
            fontSize: 18,
            lineHeight: 1.5,
            maxWidth: 400,
            opacity: 0.9,
            color: theme.palette.mode === 'dark' ? colors.grey[300] : colors.grey[700],
          }}
        >
          Connectez-vous pour accéder à votre espace personnel et gérer vos activités en toute simplicité.
        </Typography>
      </Grid>

      {/* Right Section */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div>
          <Paper
            elevation={5}
            sx={{
              padding: { xs: 3, sm: 4 },
              width: "100%",
              maxWidth: 420,
              borderRadius: 3,
              backgroundColor: theme.palette.mode === 'dark' 
                ? `rgba(${colors.primary[900]} , 0.9)` 
                : `rgba(255, 255, 255, 0.95)`,
              backdropFilter: "blur(10px)",
              border: theme.palette.mode === 'dark'
                ? `1px solid ${colors.primary[800]}`
                : `1px solid ${colors.primary[100]}`,
              boxShadow: theme.palette.mode === 'dark'
                ? `0 15px 35px ${colors.primary[900]}40`
                : "0 15px 35px rgba(0, 0, 0, 0.1)",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              position: "relative",
              overflow: "hidden",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: theme.palette.mode === 'dark'
                  ? `0 20px 45px ${colors.primary[900]}60`
                  : "0 20px 45px rgba(0, 0, 0, 0.15)",
              },
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #166534 0%, #22c55e 100%)",
              }
            }}
          >
          <form onSubmit={handleSignIn}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <LockOutlined 
                sx={{ 
                  fontSize: 40, 
                  color: theme.palette.mode === 'dark' ? colors.grey[100] : colors.grey[900],
                  backgroundColor: theme.palette.mode === 'dark' ? colors.grey[800] : 'white',
                  borderRadius: '50%',
                  padding: 1.5,
                  mb: 1
                }} 
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.mode === 'dark' ? colors.grey[100] : colors.grey[900],
                  letterSpacing: '0.5px',
                  mb: 0.5
                }}
              >
                Se Connecter
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Entrez vos identifiants pour continuer
              </Typography>
            </Box>
            <TextField
              type="email"
              label="Email"
              variant="outlined"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <EmailOutlined 
                    sx={{ 
                      color: 'action.active', 
                      mr: 1, 
                      my: 0.5 
                    }} 
                  />
                ),
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  transition: 'all 0.3s',
                  "&:hover fieldset": {
                    borderColor: theme.palette.mode === 'dark' ? colors.primary[800] : "#22c55e",
                    boxShadow: theme.palette.mode === 'dark' ? `0 0 0 4px ${colors.primary[900]}40` : "0 0 0 4px rgba(34, 197, 94, 0.1)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.mode === 'dark' ? colors.primary[900] : "#166534",
                    borderWidth: "2px",
                    boxShadow: theme.palette.mode === 'dark' ? `0 0 0 4px ${colors.primary[900]}40` : "0 0 0 4px rgba(34, 197, 94, 0.1)",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: '12px 14px',
                },
                "& .MuiInputLabel-root": {
                  color: 'text.secondary',
                },
                "& .MuiOutlinedInput-input::placeholder": {
                  color: theme.palette.mode === 'dark' ? colors.grey[300] : colors.grey[700],
                  opacity: 0.9,
                  fontSize: '0.9rem',
                },
              }}
            />

            <TextField
              type={showPassword ? "text" : "password"}
              label="Mot de passe"
              variant="outlined"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <LockOutlined 
                    sx={{ 
                      color: 'action.active', 
                      mr: 1, 
                      my: 0.5 
                    }} 
                  />
                ),
                endAdornment: (
                  <IconButton 
                    onClick={() => setShowPassword(!showPassword)} 
                    edge="end"
                    sx={{
                      color: 'action.active',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)'
                      }
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
              sx={{
                mb: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  transition: 'all 0.3s',
                  "&:hover fieldset": {
                    borderColor: "#22c55e",
                    boxShadow: "0 0 0 4px rgba(34, 197, 94, 0.1)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#166534",
                    borderWidth: "2px",
                    boxShadow: "0 0 0 4px rgba(34, 197, 94, 0.1)",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: '12px 14px',
                },
                "& .MuiInputLabel-root": {
                  color: 'text.secondary',
                },
              }}
            />

            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              mb: 2,
              mt: 1
            }}>
              <Link 
                component={RouterLink} 
                to="/forgot-password" 
                variant="body2"
                sx={{
                  color: '#22c55e',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                  }
                }}
              >
                Mot de passe oublié ?
              </Link>
            </Box>

            <div style={{ width: '100%' }}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isLoading || isSubmitting}
                sx={{
                  marginTop: 1,
                  padding: { xs: '10px 0', sm: '12px 0' },
                  background: "linear-gradient(135deg, #166534 0%, #22c55e 100%)",
                  color: "#fff",
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  letterSpacing: '0.5px',
                  boxShadow: '0 4px 12px rgba(30, 58, 138, 0.2)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: "linear-gradient(135deg, #14532d 0%, #16a34a 100%)",
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(30, 58, 138, 0.4)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    boxShadow: '0 4px 12px rgba(30, 58, 138, 0.2)',
                  },
                  '&.Mui-disabled': {
                    background: '#e0e0e0',
                    color: '#9e9e9e',
                    boxShadow: 'none',
                  },
                }}
              >
                {isLoading || isSubmitting ? (
                  <>
                    <CircularProgress size={20} thickness={4} sx={{ color: 'white', mr: 1 }} />
                    Connexion...
                  </>
                ) : 'Se connecter'}
              </Button>
            </div>
          </form>
        </Paper>
      </div>
      </Grid>
    </Grid>
    </div>
  )
}

export default Login
